// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { useState } from 'react'; // eslint-disable-line no-unused-vars
import { first, last } from 'lodash';

import { Colors } from '@zapier/theme';
import { getInitialsFromString } from '@zapier/common-utils';
import { jsx } from '@emotion/core';
import { Icon, StaticImage } from '@zapier/common-components';

/** @jsx jsx */

export type AvatarProps = {
  /** Name of user */
  name: string,
  /** Size in pixels */
  size: number,
  /** URL to image */
  url?: string,
};

const getNameInitials = (name: string): string => {
  const initials = getInitialsFromString(name.trim());
  const firstLetter = first(initials);

  if (!firstLetter) {
    return '';
  }

  return initials.length === 1
    ? firstLetter
    : `${firstLetter}${last(initials)}`;
};

const avatarCss = size => ({
  alignItems: 'center',
  background: Colors.adamantium,
  borderRadius: 3,
  display: 'inline-flex',
  height: size,
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
  width: size,
});

const imageCss = size => ({
  borderRadius: 'inherit',
  display: 'block',
  height: size,
  position: 'relative',
  width: size,
});

const initialsCss = size => ({
  color: Colors.black,
  fontSize: Math.floor(size * 0.45),
  fontWeight: 600,
  textTransform: 'uppercase',
});

/**
 * Displays a user's avatar.
 */
const Avatar = (props: AvatarProps) => {
  // Used to fall back to initials if the image fails to load
  const [hasError, setHasError] = useState(false);

  const { name, url, size } = props;
  const initials = name ? getNameInitials(name) : null;

  const showImage = url && !hasError;

  return (
    <div css={avatarCss(size)}>
      {showImage ? (
        <StaticImage
          alt={name}
          css={imageCss(size)}
          onError={() => setHasError(true)}
          src={url || ''}
        />
      ) : initials ? (
        <span css={initialsCss(size)}>{initials}</span>
      ) : (
        <Icon color="batmobile" name="user" size={size * 0.75} />
      )}
    </div>
  );
};

Avatar.defaultProps = {
  size: 36,
};

export default Avatar;
